import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../../../../../store';
import {
  getChiaveRichiestaAppuntamenti,
  ParametriRichiestaAppuntamenti,
} from '../../../utility_objects/ChiaveRichiestaAppuntamenti';
import { PersonalSelectedFiltersState } from '../store/interface';

/** Filtri del calendario personale che decidono quali appuntamenti caricare. */
export type FiltriAppuntamentiPersonale = Pick<
  PersonalSelectedFiltersState,
  'selectedDate' | 'view' | 'selectedServices' | 'selectedUsers'
> & {
  /** Email del cliente filtrato, se c'è */
  email?: string;
};

/**
 * Parametri della GetAll del calendario personale. Li usano il caricamento e
 * il confronto con la lista in redux (selectAppuntamentiPersonalePronti):
 * costruiti in un posto solo, la chiave della pagina e quella della richiesta
 * coincidono. Il Personale non filtra per punto vendita né per studio.
 */
export function getParametriAppuntamentiPersonale({
  selectedDate,
  view,
  selectedServices,
  selectedUsers,
  email,
}: FiltriAppuntamentiPersonale): ParametriRichiestaAppuntamenti {
  return {
    selectedDate,
    checkedServices: selectedServices,
    checkedUsers: selectedUsers.map((user) => user.id),
    checkedPointOfSales: [],
    checkedStudi: [],
    view,
    email,
  };
}

// Ricalcolata solo quando cambia uno dei filtri, non a ogni azione dello store
const selectChiaveAppuntamentiPersonale = createSelector(
  [
    (state: RootState) => state.bluBookingPersonalCalendar.selectedDate,
    (state: RootState) => state.bluBookingPersonalCalendar.view,
    (state: RootState) => state.bluBookingPersonalCalendar.selectedServices,
    (state: RootState) => state.bluBookingPersonalCalendar.selectedUsers,
    (state: RootState) => state.bluBookingPersonalCalendar.client?.email,
  ],
  (selectedDate, view, selectedServices, selectedUsers, email) =>
    getChiaveRichiestaAppuntamenti(
      getParametriAppuntamentiPersonale({
        selectedDate,
        view,
        selectedServices,
        selectedUsers,
        email,
      })
    )
);

/**
 * La lista appuntamenti in redux è quella della data e dei filtri correnti del
 * calendario personale. Il caricamento finito non basta: nei 50 ms prima della
 * richiesta, e finché la risposta nuova non arriva, la lista è quella vecchia
 * (anche quella di un altro calendario: la lista è una sola). Dopo un errore la
 * lista vuota ha la chiave giusta, quindi è pronta.
 */
export const selectAppuntamentiPersonalePronti = (state: RootState): boolean =>
  state.bluBookingaAppointments.chiaveRichiesta ===
  selectChiaveAppuntamentiPersonale(state);
